import { createClient } from "@openauthjs/openauth/client";

/**
 * OpenAuth client for the customer portal. Talks to our self-hosted issuer
 * at `auth.pahfacturar.com` (production) or `localhost:8581` (dev — see
 * docs/LOCAL_DEV.md for the 8580-8589 port map).
 *
 * Storage keys are namespaced with `pahfacturar.app.*` so they never
 * collide with sibling pahventure portals if opened in the same browser.
 */
const issuer =
  import.meta.env.VITE_AUTH_ISSUER_URL ??
  (import.meta.env.DEV
    ? "http://localhost:8581"
    : "https://auth.pahfacturar.com");

const clientID = import.meta.env.VITE_OAUTH_CLIENT_ID ?? "pahfacturar-app";

export const authClient = createClient({ clientID, issuer });

export const REDIRECT_URI = `${window.location.origin}/auth/callback`;

const TOKEN_KEY = "pahfacturar.app.tokens";
const CHALLENGE_KEY = "pahfacturar.app.challenge";
const POST_LOGIN_REDIRECT_KEY = "pahfacturar.app.postLoginRedirect";

export type Tokens = { access: string; refresh: string };

export function loadTokens(): Tokens | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? (JSON.parse(raw) as Tokens) : null;
  } catch {
    return null;
  }
}

export function saveTokens(tokens: Tokens) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
}

export function saveChallenge(challenge: unknown) {
  sessionStorage.setItem(CHALLENGE_KEY, JSON.stringify(challenge));
}

export function takeChallenge(): { verifier: string; state: string } | null {
  const raw = sessionStorage.getItem(CHALLENGE_KEY);
  sessionStorage.removeItem(CHALLENGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/** Stash where the user was trying to go before the auth redirect. */
export function pushPostLoginRedirect(path: string) {
  if (path && path !== "/login" && !path.startsWith("/auth/")) {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
  }
}

/** Retrieve and clear the redirect target queued by `pushPostLoginRedirect`. */
export function popPostLoginRedirect(): string | null {
  const value = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
  sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
  return value;
}
